// ============================================
// utils/iconosCategorias.js — emoji por nombre de categoría
// ============================================
// Mapa de coincidencias exactas (sin acentos importa, todo lowercased)
// + fallback por keywords + default 📦.
//
// UI-Z-A: ademas del mapa emoji legacy (iconoCategoria), se expone el
// componente Lucide <IconoCategoria> (monocromo, acepta prop color).

import React from 'react';
import {
  Boxes, Video, Siren, Network, Lock, Flame, Zap, Bot, Satellite, Megaphone,
  Cable, Cctv, BellRing, Wifi, Construction, HardHat, Truck, Building2,
  RadioTower, Tag,
} from 'lucide-react';

const ICONOS_MAP = {
  // Categorías raíz Syscom
  'audio y video profesional': '📷',
  'audio video profesional': '📷',
  'automatizacion e intrusion': '🔔',
  'automatización e intrusión': '🔔',
  'cableado estructurado': '💻',
  'control de acceso': '🔒',
  'deteccion de fuego': '⚠️',
  'detección de fuego': '⚠️',
  'energia / herramientas': '⚡',
  'energía / herramientas': '⚡',
  'energia herramientas': '⚡',
  'industria / bms / robots': '📦',
  'industria bms robots': '📦',
  'iot / gps / telematica y senalizacion audiovisual': '📱',
  'iot / gps / telemática y señalización audiovisual': '📱',
  'iot gps telematica': '📱',
  'radiocomunicacion': '📡',
  'radiocomunicación': '📡',
  'redes e it': '🌐',
  'videovigilancia': '📹',

  // Categorías Beep
  'catalogo beep': '🏢',
  'catálogo beep': '🏢',
  'cableado': '🔌',
  'mano de obra': '🔨',
  'cctv': '📹',
  'alarmas': '🚨',
  'redes': '🌐',
  'obra civil': '🧱',
  'mano de obra general': '🔨',
  'traslados': '🚗',
  'proyecto integral': '🏢',

  // Subcategorías comunes Syscom
  'camaras ip y nvrs': '📷',
  'cámaras ip y nvrs': '📷',
  'camaras y dvrs hd': '📺',
  'cámaras y dvrs hd': '📺',
  'cables y conectores': '🔌',
  'kits sistemas completos': '📦',
  'kits- sistemas completos': '📦',
  'monitores pantallas y mobiliario': '🖥️',
  'monitores y pantallas': '🖥️',
  'servidores almacenamiento': '💾',
  'servidores / almacenamiento': '💾',
  'software vms y analiticas': '💿',
  'software vms y analíticas': '💿',
  'protección contra descargas': '⚡',
  'proteccion contra descargas': '⚡',
};

// Quita acentos para comparar más laxo
function normalizar(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim();
}

export function iconoCategoria(nombre) {
  if (!nombre) return '📦';
  const key = normalizar(nombre);
  if (!key) return '📦';

  // Match exacto (con o sin acentos)
  if (ICONOS_MAP[nombre.toLowerCase().trim()]) return ICONOS_MAP[nombre.toLowerCase().trim()];
  for (const [k, v] of Object.entries(ICONOS_MAP)) {
    if (normalizar(k) === key) return v;
  }

  // Match parcial (uno contiene al otro)
  for (const [k, v] of Object.entries(ICONOS_MAP)) {
    const kn = normalizar(k);
    if (key.includes(kn) || kn.includes(key)) return v;
  }

  // Fallback por keywords
  if (key.includes('camara') || key.includes('video')) return '📹';
  if (key.includes('audio') || key.includes('sonido')) return '🔊';
  if (key.includes('cable')) return '🔌';
  if (key.includes('red') || key.includes('network') || key.includes('switch') || key.includes('router')) return '🌐';
  if (key.includes('alarma') || key.includes('intrusion')) return '🚨';
  if (key.includes('acceso') || key.includes('puerta') || key.includes('biom')) return '🔒';
  if (key.includes('fuego') || key.includes('humo') || key.includes('incendio')) return '⚠️';
  if (key.includes('energia') || key.includes('bateria') || key.includes('ups') || key.includes('power')) return '⚡';
  if (key.includes('herramienta')) return '🔧';
  if (key.includes('telefon') || key.includes('radio')) return '📡';
  if (key.includes('movil') || key.includes('iot') || key.includes('gps')) return '📱';
  if (key.includes('servidor') || key.includes('almacen') || key.includes('storage')) return '💾';
  if (key.includes('monitor') || key.includes('pantalla') || key.includes('display')) return '🖥️';
  if (key.includes('software') || key.includes('vms')) return '💿';
  if (key.includes('kit')) return '📦';
  if (key.includes('proteccion') || key.includes('descarga')) return '🛡️';
  if (key.includes('industria') || key.includes('robot') || key.includes('bms')) return '🤖';
  if (key.includes('drone') || key.includes('uav')) return '🚁';
  if (key.includes('bala') || key.includes('domo') || key.includes('ptz') || key.includes('eyeball')) return '📷';
  if (key.includes('antena') || key.includes('wifi')) return '📡';
  if (key.includes('mano') || key.includes('obra')) return '🔨';
  if (key.includes('cctv')) return '📹';

  return '📦';
}

// ============================================
// IconoCategoria — componente Lucide monocromo (reemplaza al emoji)
// ============================================
// Mapa nombre-normalizado -> componente Lucide. Sin match -> Tag (default).
// Nota de colision: "control de acceso" existe como top-level y como subcat;
// al ser el mismo nombre normalizado se resuelve a Lock (Fingerprint queda sin uso).
const MAPA = {
  // Top-level
  'catalogo beep': Boxes,
  'audio y video profesional': Video,
  'audio video profesional': Video,
  'automatizacion e intrusion': Siren,
  'cableado estructurado': Network,
  'control de acceso': Lock,
  'deteccion de fuego': Flame,
  'energia / herramientas': Zap,
  'energia herramientas': Zap,
  'industria / bms / robots': Bot,
  'industria bms robots': Bot,
  'iot / gps / telematica y senalizacion audiovisual': Satellite,
  'iot gps telematica': Satellite,
  'radiocomunicacion': RadioTower,
  'redes e it': Wifi,
  'videovigilancia': Cctv,
  'marketing': Megaphone,
  // Subcategorias
  'cableado': Cable,
  'cctv': Cctv,
  'alarmas': BellRing,
  'redes': Wifi,
  'obra civil': Construction,
  'mano de obra': HardHat,
  'mano de obra general': HardHat,
  'traslados': Truck,
  'proyecto integral': Building2,
};

export function IconoCategoria({ nombre, size = 20, color }) {
  const Ico = MAPA[normalizar(nombre)] || Tag;
  return <Ico size={size} color={color} strokeWidth={2} style={{ verticalAlign: 'middle', flexShrink: 0 }} />;
}

export default iconoCategoria;
